import { format } from "date-fns";
import axios from "axios";
import React, { useEffect, useState, useRef } from "react";
import { STATES } from "@upsolve/shared";

import SimpleTable from "../Tables/SimpleTable";
import { TD, TH } from "../Tables/TableCells";
import { H5 } from "../Type/Display.h";

const MEDIAN_INCOME_API_ENDPOINT = "/v1/content/medianIncome";
const DISPLAY_HOUSEHOLD_SIZE_LIMIT = 10;
const HOUSEHOLD_SIZES = Array.from({ length: DISPLAY_HOUSEHOLD_SIZE_LIMIT }, (_, i) => i + 1);
const CACHE_KEY_PREFIX = "medianIncome_";
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000;
const CURRENT_YEAR = format(new Date(), "yyyy");

function getCachedData(stateCode) {
  if (typeof window === "undefined") return null;
  try {
    const cached = localStorage.getItem(`${CACHE_KEY_PREFIX}${stateCode}`);
    if (!cached) return null;
    const { data, timestamp } = JSON.parse(cached);
    if (Date.now() - timestamp > CACHE_DURATION_MS) {
      localStorage.removeItem(`${CACHE_KEY_PREFIX}${stateCode}`);
      return null;
    }
    return data;
  } catch {
    return null;
  }
}

function setCachedData(stateCode, data) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(`${CACHE_KEY_PREFIX}${stateCode}`, JSON.stringify({ data, timestamp: Date.now() }));
  } catch {
    // Fail silently - caching is optional, component will work without it
  }
}

const StateMedianIncomeChart = ({ state }) => {
  const stateCode = state?.toUpperCase();
  const [incomeData, setIncomeData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (!stateCode) {
      setIsLoading(false);
      return;
    }

    const cachedData = getCachedData(stateCode);
    if (cachedData) {
      setIncomeData(cachedData);
      setIsLoading(false);
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    async function fetchMedianIncomeData() {
      try {
        setIsLoading(true);
        setError(null);

        const promises = HOUSEHOLD_SIZES.map(async (householdSize) => {
          try {
            const response = await axios.get(
              `${UPSOLVE_API_URL}${MEDIAN_INCOME_API_ENDPOINT}/${stateCode}/${householdSize}`,
              {
                signal: abortController.signal,
              }
            );
            return { householdSize, medianIncome: response.data.medianIncome };
          } catch (error) {
            if (axios.isCancel(error)) {
              throw error;
            }
            console.warn(`Failed to fetch median income for household size ${householdSize}:`, error);
            return { householdSize, medianIncome: null };
          }
        });

        const results = await Promise.all(promises);
        const incomeDataMap = {};
        let hasData = false;
        results.forEach(({ householdSize, medianIncome }) => {
          if (medianIncome !== null) {
            incomeDataMap[householdSize] = medianIncome;
            hasData = true;
          }
        });

        if (!hasData) {
          throw new Error(
            `Unable to fetch median income data for ${STATES[stateCode] || stateCode}. Please try again later.`
          );
        }

        setIncomeData(incomeDataMap);
        setCachedData(stateCode, incomeDataMap);
      } catch (error) {
        if (axios.isCancel(error)) {
          return;
        }
        const errorMessage = error instanceof Error ? error.message : "Failed to load income data";
        console.error(`Failed to fetch median income data for state ${stateCode}:`, error);
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    }

    fetchMedianIncomeData();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [stateCode]);

  const tableHeader = (
    <>
      <tr>
        <TH colSpan="3" className="header">
          <H5>
            {STATES[stateCode]} Median Income Standards for Means Test for Cases Filed In {CURRENT_YEAR}
          </H5>
        </TH>
      </tr>
      <tr>
        <TH>Household Size</TH>
        <TH>Monthly Income</TH>
        <TH>Annual Income</TH>
      </tr>
    </>
  );

  const tableRows = !incomeData
    ? null
    : HOUSEHOLD_SIZES.map((householdSize) => {
        const annualIncome = incomeData[householdSize];
        if (annualIncome === undefined || annualIncome === null) return null;
        const monthlyIncome = annualIncome / 12;

        return (
          <tr key={householdSize}>
            <TD>{householdSize}</TD>
            <TD>
              $
              {monthlyIncome.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </TD>
            <TD>
              $
              {annualIncome.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </TD>
          </tr>
        );
      }).filter(Boolean);

  let bodyContent;
  if (isLoading) {
    bodyContent = (
      <tr>
        <TD colSpan="3">Loading...</TD>
      </tr>
    );
  } else if (error || !incomeData) {
    bodyContent = (
      <tr>
        <TD colSpan="3">{error || "Unable to load income data. Please refresh the page."}</TD>
      </tr>
    );
  } else {
    bodyContent = tableRows;
  }

  return (
    <SimpleTable>
      <thead>{tableHeader}</thead>
      <tbody>{bodyContent}</tbody>
    </SimpleTable>
  );
};

export default StateMedianIncomeChart;
